import {
  ConditionRule,
  FilterRule,
  OperatorType,
  ReviewDetails,
  ReviewFiltersRequestV2,
  ReviewSummary,
  SortBy,
  StarRating,
} from './types';
import { Foundation } from '../../.generated/Foundation.EnterpriseWeb.model';
import { fetchInternalAPIWithQueryString } from 'lib/utils/api-request-utils';
import useSWR from 'swr';
import { RuleContext, RuleList } from 'lib/rules/rules';
import { getRulesFromString } from 'lib/rules/serialization';
import { RulesConstants } from 'lib/constants/rules';
import { OrCondition } from 'lib/rules/conditions';
import { isNullOrWhitespace } from 'lib/utils/string-utils';
import { deepSearch } from 'lib/utils/object-utils';

const apiUrl = typeof window === 'undefined' ? process.env.EW_ENTERPRISE_WEB_APIv2_URL : '';

/** For use with components that retrieve review data client-side. Utilizes SWR for efficient data retrieval and caching */
export const useReviewDetails = (filters: ReviewFiltersRequestV2): ReviewDetails[] | null => {
  if (!filters.filterRules) {
    // return early if no review filters
    return null;
  }
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(filters),
  };
  const fetcher = () =>
    fetchInternalAPIWithQueryString(
      `${apiUrl}/api/rba/reviews/get-review-details-v2`,
      requestOptions
    ).then((res) => res.json());
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data } = useSWR(requestOptions.body, fetcher);
  return data;
};

/** For use with components that retrieve review data client-side. Utilizes SWR for efficient data retrieval and caching */
export const useReviewSummary = (filters: FilterRule): ReviewSummary | null => {
  if (!filters) {
    // return early if no review filters
    return null;
  }
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(filters),
  };

  const fetcher = () =>
    fetchInternalAPIWithQueryString(
      `${apiUrl}/api/rba/reviews/get-reviews-summary-v2`,
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ReviewSummary[]) => {
        const parsedData = aggregateReviewSummary(data);
        return parsedData;
      });
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data } = useSWR(requestOptions.body, fetcher);

  return data ? data : null;
};

/** For use with components that retrieve review data server side (getStaticProps) */
export async function getReviewsDetails(filters: ReviewFiltersRequestV2): Promise<ReviewDetails[]> {
  if (!filters) {
    return [];
  }

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(filters),
  };

  const response = await fetchInternalAPIWithQueryString(
    `${apiUrl}/api/rba/reviews/get-review-details-v2`,
    requestOptions
  );
  const responseData: ReviewDetails[] = await response?.json();

  return responseData;
}

/** For use with components that retrieve review data server side (getStaticProps) */
export async function getReviewsSummary(filters?: FilterRule): Promise<ReviewSummary | null> {
  if (!filters) {
    return null;
  }

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(filters),
  };

  const response = await fetchInternalAPIWithQueryString(
    `${apiUrl}/api/rba/reviews/get-reviews-summary-v2`,
    requestOptions
  );
  const responseData: ReviewSummary[] = await response?.json();
  const parsedReview = aggregateReviewSummary(responseData);

  return parsedReview;
}

export type ReviewFilters =
  Foundation.EnterpriseWeb.RenewalByAndersen.FieldSets.Reviews.ReviewFilters & {
    pageSize: number;
    pageNumber: number;
    sortBy: SortBy[];
    exactRating: number;
    minimumCharacters: number;
  };

export function parseReviewFilterData({
  reviewFilterProps,
  reviewFilters,
}: {
  reviewFilterProps: ReviewFilters;
  reviewFilters: string;
}) {
  const rules = getRulesFromString(reviewFilters);
  const context = setupRuleContext(rules);
  rules.run(context);

  const parsedFilters: ReviewFiltersRequestV2 = {
    pageSize: reviewFilterProps.pageSize,
    pageNumber: reviewFilterProps.pageNumber,
    sortBy: reviewFilterProps.sortBy,
    filterRules: {
      operator: context.parameters[RulesConstants.Parameters.Operator] as OperatorType,
      conditions: context.parameters[RulesConstants.Parameters.Conditions] as Array<ConditionRule>,
    },
  };

  return parsedFilters;
}

function setupRuleContext(rules: RuleList<RuleContext>): RuleContext {
  const context = new RuleContext();

  context.parameters[RulesConstants.Parameters.Operator] =
    rules.rules[0]?.condition instanceof OrCondition
      ? RulesConstants.Operators.Or
      : RulesConstants.Operators.And;

  return context;
}

export function parseFilterRuleDataForAggregates(
  productIds?: string[],
  affiliateIds?: string[]
): FilterRule {
  let id: string[];

  if (productIds && affiliateIds && productIds.length > 0 && affiliateIds.length > 0) {
    id = productIds
      .map((productId: string) => {
        return affiliateIds.map((affiliateId: string) => {
          return affiliateId && !isNullOrWhitespace(affiliateId)
            ? affiliateId + '_' + productId
            : productId;
        });
      })
      .flat() as unknown as string[];
  } else {
    id = (productIds?.length ?? 0 > 0 ? productIds : affiliateIds) as string[];
  }

  const condition: ConditionRule = {
    propertyName: 'id',
    comparison: Array.isArray(id) ? 'in' : 'equals',
    value: id,
  };
  const reviewFilter: FilterRule = {
    operator: 'AND',
    conditions: [condition],
  };
  return reviewFilter;
}

function aggregateReviewSummary(reviewSummary: ReviewSummary[]): ReviewSummary {
  let emptyRatings = 0;
  const reviewSum: ReviewSummary = reviewSummary?.reduce((acc, curr) => {
    if (!curr?.averageRating) {
      emptyRatings += 1;
    }

    acc.averageRating += curr?.averageRating ?? 0;
    acc.totalProjects += curr?.totalProjects ?? 0;
    acc.totalReviews += curr?.totalReviews ?? 0;

    acc.starRatings = curr.starRatings.map((rating: StarRating) => {
      const accRating = acc.starRatings.find((x: StarRating) => x.rating == rating.rating);
      return {
        rating: rating.rating,
        numberOfRatings: (accRating?.numberOfRatings ?? 0) + (rating.numberOfRatings ?? 0),
      };
    });

    return acc;
  });

  const numOfTruRatings =
    reviewSummary?.length - emptyRatings > 0 ? reviewSummary?.length - emptyRatings : 0;

  if (reviewSum) {
    reviewSum.averageRating = parseFloat(
      numOfTruRatings > 0
        ? (reviewSum?.averageRating / numOfTruRatings).toFixed(1)
        : reviewSum?.averageRating.toFixed(1)
    );
  }

  return reviewSum;
}

export function createReviewSummaryFilter(filters: FilterRule): FilterRule {
  const productConditions = deepSearch<ConditionRule>(filters.conditions, (x: ConditionRule) => {
    return x.propertyName?.toLowerCase().includes('productname') ?? false;
  });

  const products = productConditions.map((p: ConditionRule) => {
    return p.value;
  }) as string[];

  const affiliateConditions = deepSearch<ConditionRule>(filters.conditions, (x: ConditionRule) => {
    return x.propertyName?.toLowerCase().includes('affiliateid') ?? false;
  });

  const affiliates = affiliateConditions.map((p: ConditionRule) => {
    return p.value?.toString();
  }) as string[];

  const newFilterRule = parseFilterRuleDataForAggregates(products, affiliates);

  return newFilterRule;
}
